// src/Views/Results/Results.js
// Updated: January 22, 2026 - Removed Football tab, Crypto AI & Polymarket only
import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import './Results.css';
import { db } from '../../firebase';
import {
    collection,
    onSnapshot,
    query,
    where,
    limit
} from 'firebase/firestore';

const Results = () => {
    const [filter, setFilter] = useState('all'); // all | tp_hit | sl_hit
    const [cryptoItems, setCryptoItems] = useState([]);
    const [loadingCrypto, setLoadingCrypto] = useState(true);
    const [errorCrypto, setErrorCrypto] = useState('');

    const normCryptoStatus = (v) => {
        const s = String(v || '').toLowerCase();
        if (s === 'tp_hit' || s === 'sl_hit') return s;
        return 'pending';
    };

    const mapResolvedAt = (data) => {
        const ra = data?.resolvedAt;
        if (ra && typeof ra.toMillis === 'function') return ra.toMillis();

        const ua = data?.updatedAt;
        if (ua && typeof ua.toMillis === 'function') return ua.toMillis();

        const ca = data?.createdAt;
        if (ca && typeof ca.toMillis === 'function') return ca.toMillis();

        return 0;
    };

    const sortByResolved = (a, b) => {
        const at = a.resolvedAtMs || 0;
        const bt = b.resolvedAtMs || 0;
        return bt - at;
    };

    // ----- Crypto results (crypto_trades) -----
    useEffect(() => {
        setLoadingCrypto(true);
        setErrorCrypto('');

        const q = query(
            collection(db, 'crypto_trades'),
            where('status', 'in', ['tp_hit', 'sl_hit']),
            limit(100)
        );

        const unsub = onSnapshot(
            q,
            (snapshot) => {
                const docs = snapshot.docs.map((d) => {
                    const data = d.data();
                    const marketType = String(data.marketType || '').toLowerCase();

                    // --- 🛡️ NAME RESOLUTION FIX ---
                    // 1. Determine "League" (Top small text) - e.g. BTC/USDT or Polymarket
                    let pair = 'Crypto';
                    if (data.pairLabel) pair = data.pairLabel;
                    else if (data.symbol) pair = data.symbol;
                    else if (marketType === 'polymarket') pair = 'Polymarket';
                    else if (marketType === 'memecoin') pair = 'Memecoins';

                    // 2. Determine "Match" (Big bold text) - e.g. Long Setup or The Question
                    let title = 'Trade Record';
                    if (data.title) title = data.title;
                    else if (data.marketQuestion) title = data.marketQuestion;
                    else if (data.pairLabel) title = data.pairLabel; // Fallback to pair if no title
                    else if (data.symbol) title = `${data.symbol} Trade`;

                    // Entry zone logic (CEX/Memecoin)
                    const entryZone = data.entryZone
                        || (data.entry1 && data.entry2 ? `${data.entry1} – ${data.entry2}` : (data.entry1 || data.entry2 || ''));

                    // TP logic
                    const takeProfit = data.takeProfitZone || data.takeProfit || '';

                    return {
                        id: d.id,
                        status: normCryptoStatus(data.status),
                        resolvedAtMs: mapResolvedAt(data),
                        marketType,
                        pair,
                        title,
                        // Polymarket
                        setup: data.title || data.marketQuestion || '',
                        venue: data.polymarketUrl || data.marketOutcome || '',
                        // CEX/Memecoin
                        entryZone,
                        takeProfit
                    };
                }).sort(sortByResolved);

                setCryptoItems(docs);
                setLoadingCrypto(false);
            },
            (err) => {
                console.error('Results crypto snapshot error:', err);
                setErrorCrypto('Could not load results.');
                setLoadingCrypto(false);
            }
        );

        return () => unsub();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredCrypto = useMemo(() => {
        if (filter === 'all') return cryptoItems;
        return cryptoItems.filter((x) => x.status === filter);
    }, [cryptoItems, filter]);

    const filterButtons = [
        { key: 'all', label: 'All' },
        { key: 'tp_hit', label: 'TP Hit' },
        { key: 'sl_hit', label: 'SL Hit' }
    ];

    const cryptoStatusLabel = (s) => {
        if (s === 'tp_hit') return 'TP Hit';
        if (s === 'sl_hit') return 'SL Hit';
        return String(s || '').toUpperCase();
    };

    const cryptoMarketLabel = (mt) => {
        if (mt === 'polymarket') return 'Polymarket';
        if (mt === 'cex') return 'Bybit/Binance';
        if (mt === 'memecoin') return 'Memecoins';
        return 'Crypto';
    };

    return (
        <div className="results">
            <div className="results__inner">

                <header className="results__header">
                    <div className="results__pill">
                        <Icon icon="solar:cup-first-linear" className="results__pill-icon" />
                        <span>Performance Log</span>
                    </div>

                    <h1 className="results__title">Verified Outcomes</h1>
                    <p className="results__subtitle">
                        A transparent record of every resolved AI prediction.
                    </p>
                </header>

                <div className="results__controls">
                    {/* Single Category Header */}
                    <div className="results__tabs">
                        <button
                            type="button"
                            className="results__tab results__tab--active"
                            style={{ cursor: 'default' }}
                        >
                            <Icon icon="solar:bitcoin-linear" className="results__tab-icon" />
                            <span>Crypto AI & Polymarket</span>
                        </button>
                    </div>

                    <div className="results__filters">
                        {filterButtons.map((b) => (
                            <button
                                key={b.key}
                                type="button"
                                className={`results__filter ${filter === b.key ? 'results__filter--active' : ''}`}
                                onClick={() => setFilter(b.key)}
                            >
                                {b.label}
                            </button>
                        ))}
                    </div>
                </div>

                <section className="results__panel">
                    {errorCrypto && (
                        <p className="results__error">{errorCrypto}</p>
                    )}

                    {loadingCrypto && (
                        <div className="results__empty">
                            <div className="results__spinner" />
                            <p>Loading results…</p>
                        </div>
                    )}

                    {!loadingCrypto && filteredCrypto.length === 0 && (
                        <div className="results__empty">
                            <Icon icon="solar:shield-warning-linear" className="results__empty-icon" />
                            <p>No results found.</p>
                        </div>
                    )}

                    {!loadingCrypto && filteredCrypto.length > 0 && (
                        <ul className="results__list">
                            {filteredCrypto.map((x) => (
                                <li key={x.id} className={`results__card results__card--${x.status}`}>
                                    <div className="results__card-top">
                                        <div className="results__badge-row">
                                            <span className="results__badge-plan">
                                                {cryptoMarketLabel(x.marketType)}
                                            </span>
                                            <span className={`results__badge-status results__badge-status--${x.status}`}>
                                                {cryptoStatusLabel(x.status)}
                                            </span>
                                        </div>

                                        <div className="results__league">{x.pair}</div>
                                        <div className="results__match">{x.title}</div>
                                    </div>

                                    <div className="results__meta">
                                        {x.marketType === 'polymarket' && (
                                            <>
                                                {x.setup && (
                                                    <div className="results__chip">
                                                        <span>Setup: {x.setup}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {x.marketType === 'cex' && x.entryZone && (
                                            <div className="results__chip">
                                                <span>Entry: {x.entryZone}</span>
                                            </div>
                                        )}

                                        {x.takeProfit && (
                                            <div className="results__chip results__chip--tp">
                                                <span>TP: {x.takeProfit}</span>
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
};

export default Results;